export const formatDate = (
  date: string | Date,
  monthType: 'long' | 'numeric' = 'numeric',
  onlyDate = false,
): string => {
  try {
    if (typeof date === 'string') {
      // Garantindo que está no formato ISO 8601
      if (!date.includes('T')) {
        date = `${date}T00:00:00`;
      }
      date = new Date(date);
      // date = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    }

    const options: Intl.DateTimeFormatOptions = onlyDate
      ? {
          year: 'numeric',
          month: monthType,
          day: 'numeric',
        }
      : {
          year: 'numeric',
          month: monthType,
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        };

    const finalDate = date.toLocaleString(navigator.language || 'pt', options);
    return finalDate;
  } catch (error) {
    console.error('Erro ao formatar a data:', error);
    return '';
  }
};

export const formatTime = (
  date: string | Date,
  formatTime = 'HH:MM',
): string => {
  try {
    // Converter string para objeto Date
    if (typeof date === 'string') {
      // Ajustar o formato do fuso horário (+0000 para +00:00)
      const formattedDate = date.replace(/(\+|-)(\d{2})(\d{2})$/, '$1$2:$3');
      date = new Date(formattedDate); // Criar o objeto Date
    }

    // Validar se o objeto Date é válido
    if (!date || isNaN(date.getTime())) {
      return 'Inválida';
    }

    // Extrair horas, minutos e segundos com zero à esquerda
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    // Substituir no formato especificado
    return formatTime
      .replace(/HH/g, hours)
      .replace(/MM/g, minutes)
      .replace(/SS/g, seconds);
  } catch (error) {
    console.error('Erro ao formatar a data:', error);
    return '';
  }
};

export function getWeekdayName(day: number): string {
  const weekdays = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  if (day < 0 || day > 6) return '---';

  return weekdays[day];
}

export const formatDateTime = (date: string | Date): string => {
  try {
    if (typeof date === 'string') {
      if (!date.includes('T')) {
        date = `${date}T00:00:00`;
      }
      date = new Date(date);
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };

    return date.toLocaleString(navigator.language || 'pt-BR', options);
  } catch (error) {
    console.error('Erro ao formatar data e hora:', error);
    return '';
  }
};

export const currencyMap = {
  brl: { flag: '🇧🇷', locale: 'pt-BR', currency: 'BRL' },
  usd: { flag: '🇺🇸', locale: 'en-US', currency: 'USD' },
  ars: { flag: '🇦🇷', locale: 'es-AR', currency: 'ARS' },
  mxn: { flag: '🇲🇽', locale: 'es-MX', currency: 'MXN' },
  cop: { flag: '🇨🇴', locale: 'es-CO', currency: 'COP' },
  clp: { flag: '🇨🇱', locale: 'es-CL', currency: 'CLP' },
};

export const formatCurrency = (
  value: number,
  locale: string,
  currency: string,
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);

type FormatMoneyOptions = {
  display?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  appendCode?: boolean;
  disambiguateSymbol?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

const AMBIGUOUS_SYMBOLS = new Set(['$', '£']);
const CURRENCY_PREFIX: Record<string, string> = {
  USD: 'US$',
  MXN: 'MX$',
  CAD: 'CA$',
  AUD: 'A$',
  NZD: 'NZ$',
  SGD: 'S$',
  HKD: 'HK$',
  ARS: 'AR$',
  COP: 'COP$',
  CLP: 'CLP$',
};

// cache de Intl.NumberFormat
const nfCache = new Map<string, Intl.NumberFormat>();

function getNumberFormat(
  locale: string,
  currency: string,
  display: NonNullable<FormatMoneyOptions['display']>,
  minFD?: number,
  maxFD?: number,
) {
  const key = `${locale}__${currency}__${display}__${minFD ?? ''}_${maxFD ?? ''}`;
  let nf = nfCache.get(key);
  if (!nf) {
    nf = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: display,
      minimumFractionDigits: minFD,
      maximumFractionDigits: maxFD,
    });
    nfCache.set(key, nf);
  }
  return nf;
}

// Normaliza string numérica (ex.: "1.234,56" -> "1234.56")
function toNumberSafe(value: number | string): number {
  if (typeof value === 'number') return value;
  const s = String(value).trim();
  if (/^\d{1,3}(\.\d{3})*,\d{1,2}$/.test(s)) {
    return Number(s.replace(/\./g, '').replace(',', '.'));
  }
  return Number(s.replace(/[^\d.-]/g, ''));
}

// Sua função ajustada
export const formatMoney = (
  value: number | string,
  currency?: string,
  language?: string,
  options: FormatMoneyOptions = {},
): string => {
  try {
    // defaults
    const {
      display = 'symbol',
      appendCode = false,
      disambiguateSymbol = true,
      minimumFractionDigits,
      maximumFractionDigits,
    } = options;

    const num = toNumberSafe(value);
    if (!isFinite(num)) return String(value);

    let finalCurrency = 'BRL';
    let finalLanguage = 'pt';

    if (currency) {
      finalCurrency = currency.toUpperCase();
      finalLanguage = language || 'pt';
    } else {
      try {
        const storeInfo = localStorage.getItem('store_info') || '{}';
        const storeInfoJson = JSON.parse(storeInfo);
        if ('currency' in storeInfoJson && 'language' in storeInfoJson) {
          finalCurrency = String(storeInfoJson.currency).toUpperCase();
          finalLanguage = String(storeInfoJson.language || 'pt');
        }
      } catch (_) {
        console.log('error', _);
      }
    }

    // Usa currencyMap quando existir para obter locale/currency “canonizados”
    const currencyKey = finalCurrency.toLowerCase();
    const currencyInfo = (currencyMap as any)?.[currencyKey];
    const locale = currencyInfo?.locale || finalLanguage;
    const curr = currencyInfo?.currency || finalCurrency;

    const nf = getNumberFormat(
      locale,
      curr,
      display,
      minimumFractionDigits,
      maximumFractionDigits,
    );

    // Para 'code' e 'name', não há ambiguidade
    if (!disambiguateSymbol || display === 'code' || display === 'name') {
      const base = nf.format(num);
      return appendCode && display !== 'code' ? `${base} (${curr})` : base;
    }

    // Para 'symbol'/'narrowSymbol': desambiguar via formatToParts
    const parts = nf.formatToParts(num);
    const currencyPart = parts.find((p) => p.type === 'currency');

    // Se não tiver currencyPart ou não for ambíguo, retorna direto
    if (!currencyPart || !AMBIGUOUS_SYMBOLS.has(currencyPart.value)) {
      const base = parts.map((p) => p.value).join('');
      return appendCode ? `${base} (${curr})` : base;
    }

    // Substitui o símbolo por um prefixo específico (ex.: MX$)
    const prefix = CURRENCY_PREFIX[curr] ?? `${curr} `;
    const fixed = parts
      .map((p) => (p.type === 'currency' ? prefix : p.value))
      .join('');
    return appendCode ? `${fixed} (${curr})` : fixed;
  } catch (error) {
    console.error(error);
    return String(value);
  }
};
